import React from 'react'
import { DocumentPage, Text, View, StyleSheet, useStyle } from '~/lib/pdf'
import Moment from 'moment'

export const defaultStyle = {
  xMargin: 0.4,
  yMargin: 0.4,
  size: 'Letter',
  accentColor: '#005BA2',
  lineHeightMultiplier: 1.0,
  listLineHeightMultiplier: 1.0,
  primaryFontFamily: 'Archivo',
  secondaryFontFamily: 'Archivo',
  headingFontSize: 24,
  bodyFontSize: 10,
  workHeading: 'Work Experience',
  educationHeading: 'Education',
  presentDateField: 'Present',
}

export const getOptionalEditorFields = () => ({
  basicsJobTitle: false,
  basicsLocation: false,
  basicsSummary: true,
  basicsWebsite: true,
  basicsLinkedin: true,
  volunteer: false,
  workLocation: false,
  workSummary: false,
  educationCourses: true,
  educationLocation: false,
  educationGpa: false,
  educationTenure: true,
  skillProficiency: false,
  additionalInfo: false,
  awards: false,
})

export const Template = ({ jsonDocument, templateStyle, title, locale }) => {
  const {
    secondaryFontFamily,
    accentColor,
    bodyFontSize,
    headingFontSize,
    lineHeightMultiplier,
  } = templateStyle
  const contactFontSize = (bodyFontSize * 5) / 6

  const styles = StyleSheet.create({
    headingContainer: {
      border: '0px solid #000000',
      minHeight: '90px',
    },
    heading: {
      fontFamily: secondaryFontFamily,
      fontSize: headingFontSize,
      fontWeight: 'bold',
      color: accentColor,
      lineHeight: '30.72px',
      break: 'word',
    },
    leftSection: {
      border: '0px solid #000000',
      flexBasis: '36%',
      flexGrow: 1,
      paddingRight: '40px',
    },
    rightSection: {
      flexBasis: '63%',
      flexGrow: 1,
    },
    contactSection: {
      color: accentColor,
      fontFamily: secondaryFontFamily,
      fontSize: contactFontSize,
      textAlign: 'right',
      minHeight: '90px',
    },
    contactItem: {
      paddingBottom: '4px',
    },
    sectionTitle: {
      fontSize: bodyFontSize,
      fontWeight: 'bold',
      marginTop: '16px',
    },
    skillsSection: { paddingBottom: '12px', paddingTop: '12px' },
    summarySection: {
      maxHeight: '200px',
      fontSize: bodyFontSize,
      lineHeight: `${16 * lineHeightMultiplier}px`,
      fontWeight: 'semibold',
      marginTop: '16px',
    },
  })

  const stripHttp = (url) => {
    return url?.replace(/https{0,1}:\/\//, '')
  }

  const { basics, work, skills, education } = jsonDocument
  const { name, email, phone, url, profiles, summary } = basics || {}

  const linkedinProfile =
    profiles &&
    profiles.find((element) => {
      return element.network?.toLowerCase() === 'linkedin'
    })

  const displayUrl = stripHttp(url)

  const linkedinProfileUrl = linkedinProfile && stripHttp(linkedinProfile.url)

  return (
    <DocumentPage templateStyle={templateStyle} title={title}>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.leftSection}>
          <View style={styles.headingContainer}>
            {name && (
              <Text
                style={styles.heading}
                hyphenationCallback={(word) => [word]}
              >
                {name}
              </Text>
            )}
          </View>
          <View style={styles.summarySection}>
            <Text hyphenationCallback={(word) => [word]}>{summary}</Text>
          </View>
          {skills &&
            skills.map((skill, index) => {
              return (
                <View key={`skill-${skill.name}-${index}`}>
                  <Text style={styles.sectionTitle}>{skill.name}</Text>
                  <View style={styles.skillsSection}>
                    {skill?.keywords?.map((keyword, kwIndex) => {
                      if (!keyword) return null
                      return (
                        <BulletLine key={`keyword-${keyword}-${kwIndex}`}>
                          {keyword}
                        </BulletLine>
                      )
                    })}
                  </View>
                </View>
              )
            })}
        </View>
        <View style={styles.rightSection}>
          <View style={styles.contactSection}>
            <Text style={styles.contactItem}>{email}</Text>
            <Text style={styles.contactItem}>{phone}</Text>
            <Text style={styles.contactItem}>{linkedinProfileUrl}</Text>
            <Text style={styles.contactItem}>{displayUrl}</Text>
          </View>
          <View>
            {work && work.length > 0 && (
              <WorkSection work={work} styles={styles} locale={locale} />
            )}
            {education && education.length > 0 && (
              <EducationSection
                education={education}
                styles={styles}
                locale={locale}
              />
            )}
          </View>
        </View>
      </View>
    </DocumentPage>
  )
}

const EducationSection = ({ education, styles, locale }) => {
  const {
    templateStyle: { educationHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{educationHeading}</Text>
      {education.map((edItem, index) => (
        <EducationView
          education={edItem}
          key={`education-${index}`}
          locale={locale}
        />
      ))}
    </>
  )
}

const WorkSection = ({ work, styles, locale }) => {
  const {
    templateStyle: { workHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{workHeading}</Text>
      {work.map((job, index) => (
        <JobView job={job} key={`job-${index}`} locale={locale} />
      ))}
    </>
  )
}

const JobView = ({ job, locale }) => {
  const {
    templateStyle: { accentColor, bodyFontSize },
  } = useStyle()
  const tenureFontSize = (bodyFontSize * 2) / 3

  const styles = StyleSheet.create({
    jobContainer: {
      paddingTop: '12px',
    },
    jobCompany: {
      fontSize: bodyFontSize,
      fontWeight: 'semibold',
      color: accentColor,
      paddingRight: '8px',
    },
    jobPosition: {
      fontSize: bodyFontSize,
      fontWeight: 'normal',
      color: '#000000',
    },
    jobTenure: {
      fontSize: tenureFontSize,
      fontStyle: 'italic',
      color: '#000000',
      paddingBottom: '12px',
    },
  })
  if (!job.position && !job.name) {
    return null
  }

  return (
    <View style={styles.jobContainer} wrap={false}>
      <View style={{ flexDirection: 'row' }}>
        {job?.name && <Text style={styles.jobCompany}>{job.name}</Text>}
        <Text style={styles.jobPosition}>{job.position}</Text>
      </View>
      <Text style={styles.jobTenure}>
        <Tenure item={job} locale={locale} />
      </Text>
      {job.highlights &&
        job.highlights.map((highlight, index) => (
          <BulletLine key={`highlight-${index}`}>{highlight}</BulletLine>
        ))}
    </View>
  )
}

const EducationView = ({ education, locale }) => {
  const {
    templateStyle: { accentColor, bodyFontSize },
  } = useStyle()
  const tenureFontSize = (bodyFontSize * 2) / 3

  const styles = StyleSheet.create({
    jobContainer: {
      paddingTop: '12px',
    },
    jobCompany: {
      fontSize: bodyFontSize,
      fontWeight: 'semibold',
      color: accentColor,
      paddingRight: '8px',
    },
    jobPosition: {
      fontSize: bodyFontSize,
      fontWeight: 'normal',
      color: '#000000',
    },
    jobTenure: {
      fontSize: tenureFontSize,
      fontStyle: 'italic',
      color: '#000000',
      paddingBottom: '12px',
    },
  })

  if (!education.institution && !education.studyType) {
    return null
  }

  return (
    <View style={styles.jobContainer} wrap={false}>
      <View style={{ flexDirection: 'row' }}>
        <Text style={styles.jobCompany}>{education.institution}</Text>
        <Text style={styles.jobPosition}>
          {education.studyType} {education.area}
        </Text>
      </View>
      <Text style={styles.jobTenure}>
        <Tenure item={education} locale={locale} />
      </Text>
      {education.courses &&
        education.courses.map((course, index) => (
          <BulletLine key={`highlight-${index}`}>{course}</BulletLine>
        ))}
    </View>
  )
}

const BulletLine = ({ children }) => {
  const {
    templateStyle: { listLineHeightMultiplier, bodyFontSize },
  } = useStyle()
  const bulletFontSize = (bodyFontSize * 5) / 6
  const styles = StyleSheet.create({
    highlightBullet: {
      padding: '0 4px',
      fontSize: bulletFontSize,
      fontWeight: 'normal',
      color: '#000000',
      lineHeight: `${16 * listLineHeightMultiplier}px`,
    },
    highlightText: {
      fontSize: bulletFontSize,
      fontWeight: 'normal',
      color: '#000000',
      lineHeight: `${16 * listLineHeightMultiplier}px`,
    },
    container: {
      flexDirection: 'row',
      paddingBottom: `${4 * listLineHeightMultiplier}px`,
    },
  })

  const hcbNone = (word) => [word]

  return (
    <View style={styles.container}>
      <Text style={styles.highlightBullet}>&#8226;</Text>
      <Text hyphenationCallback={hcbNone} style={styles.highlightText}>
        {children}
      </Text>
    </View>
  )
}

const momentFormat = (date, nullValue = '', locale) => {
  if (!date || [undefined, null, ''].includes(date)) {
    return nullValue
  }
  try {
    const result = Moment(date).locale(locale).format('MMM YYYY')
    if (result === 'Invalid date') return nullValue
    return result
  } catch {
    return nullValue
  }
}

const isNotBlank = (item) => ![null, undefined, ''].includes(item)

const Tenure = ({ item, locale }) => {
  const {
    templateStyle: { presentDateField },
  } = useStyle()

  if (!item.startDate && !item.endDate) {
    return null
  }

  const displayLocale = locale || 'en'
  const formattedStart = momentFormat(item.startDate, '', displayLocale)
  const formattedEnd = momentFormat(
    item.endDate,
    presentDateField,
    displayLocale,
  )

  return [formattedStart, formattedEnd].filter(isNotBlank).join(' - ')
}
